import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, DialogContent, Divider, Menu, MenuItem, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';

import remindersApi from '../../api/reminders/api';
import type { Reminder } from '../../api/reminders/types';
import { displayToast } from '../RTToast';
import { DraggableDialog } from '../DraggablePaper/DraggablePaper';
import ModalFormTitle from '../Tables/TableWithModalEdit/modalFormPieces/ModalFormTitle';
import { DialogActionsWrapper } from '../Tables/TableWithModalEdit/modalFormPieces/ModalFormActions/ModalFormActions';

const SNOOZES: Array<{ label: string; minutes: number }> = [
    { label: '10 minutes', minutes: 10 },
    { label: '1 hour', minutes: 60 },
    { label: 'Tomorrow', minutes: 24 * 60 }
];

/** "Closed" in this tab — keyed with the time, so a snoozed reminder pops again when it is due. */
const DISMISSED_KEY = 'reminders.dismissed';
const dismissKey = (r: Reminder) => `${r._id}@${r.remindAt}`;
const readDismissed = (): Set<string> => {
    try {
        return new Set(JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || '[]'));
    } catch {
        return new Set();
    }
};
const writeDismissed = (set: Set<string>) => {
    try {
        sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...set]));
    } catch {
        /* storage unavailable — the reminder may pop again after a reload, nothing worse */
    }
};

const canNotify = () => typeof window !== 'undefined' && 'Notification' in window;

interface Props {
    /** The user's due reminders (open, time has come), kept fresh by RemindersIndicator. */
    due: Reminder[];
    /** Called after an action changed a reminder, so the indicator re-reads. */
    onChanged: () => void;
}

/**
 * The pop-up a due reminder opens, on whatever page the user is on: Done, Snooze (10 min / 1 hour
 * / tomorrow), Open travel file, or Close. Close only hides it in this tab — the reminder stays
 * open and keeps counting on the ⏰ badge until it is done, snoozed or deleted. A desktop
 * notification is shown too when the browser already allows them (asked by the chat pop-ups).
 */
const ReminderDuePopup: FC<Props> = ({ due, onChanged }) => {
    const history = useHistory();
    const [dismissed, setDismissed] = useState<Set<string>>(readDismissed);
    const [snoozeFor, setSnoozeFor] = useState<{ anchor: HTMLElement; reminder: Reminder } | null>(null);
    const notified = useRef<Set<string>>(new Set());

    const showing = useMemo(() => due.filter((r) => !dismissed.has(dismissKey(r))), [due, dismissed]);

    // A desktop notification once per reminder occurrence, for a tab that is in the background.
    useEffect(() => {
        if (!canNotify() || Notification.permission !== 'granted') return;
        showing.forEach((r) => {
            const key = dismissKey(r);
            if (notified.current.has(key)) return;
            notified.current.add(key);
            if (document.visibilityState === 'visible') return;
            try {
                const n = new Notification('Reminder', { body: r.text, tag: `reminder-${key}` });
                n.onclick = () => {
                    window.focus();
                    n.close();
                };
            } catch {
                /* some browsers refuse notifications from a non-secure origin — the pop-up still shows */
            }
        });
    }, [showing]);

    const dismiss = (r: Reminder) =>
        setDismissed((prev) => {
            const next = new Set(prev).add(dismissKey(r));
            writeDismissed(next);
            return next;
        });

    const act = async (action: () => Promise<unknown>, failure: string) => {
        try {
            await action();
            onChanged();
        } catch (e: any) {
            displayToast('error', failure, e?.response?.data?.message || 'Something went wrong');
        }
    };

    if (!showing.length) return null;

    return (
        <DraggableDialog
            open
            PaperProps={{ sx: { width: 360, maxWidth: 'calc(100% - 32px)' } }}
            onClose={() => showing.forEach(dismiss)}
        >
            <ModalFormTitle
                title={showing.length === 1 ? 'Reminder' : `Reminders (${showing.length})`}
                toggleDialog={() => showing.forEach(dismiss)}
            />
            <DialogContent dividers sx={{ p: 0 }}>
                {showing.map((r, i) => (
                    <Box key={r._id}>
                        {i > 0 && <Divider />}
                        <Box sx={{ px: 3, py: 2 }}>
                            <Typography dir="auto" sx={{ whiteSpace: 'pre-wrap', fontWeight: 500, textAlign: 'start' }}>
                                {r.text}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {dayjs(r.remindAt).format('DD/MM/YYYY HH:mm')}
                                {r.travelFileRef ? ` · Travel file ${r.travelFileRef}` : ''}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => act(() => remindersApi.setCompleted(r._id, true), 'Could not complete')}
                                >
                                    Done
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => setSnoozeFor({ anchor: e.currentTarget, reminder: r })}
                                >
                                    Snooze
                                </Button>
                                {r.travelFileRef && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => {
                                            dismiss(r);
                                            history.push(
                                                `/reservations/tour/travel-file-list/travel-file?ref_number=${r.travelFileRef}`
                                            );
                                        }}
                                    >
                                        Open travel file
                                    </Button>
                                )}
                                <Button size="small" onClick={() => dismiss(r)}>
                                    Close
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                ))}
            </DialogContent>
            {showing.length > 1 && (
                <DialogActionsWrapper>
                    <Stack direction="row" justifyContent="flex-end" sx={{ width: '100%' }}>
                        <Button onClick={() => showing.forEach(dismiss)}>Close all</Button>
                    </Stack>
                </DialogActionsWrapper>
            )}
            <Menu anchorEl={snoozeFor?.anchor} open={!!snoozeFor} onClose={() => setSnoozeFor(null)}>
                {SNOOZES.map((s) => (
                    <MenuItem
                        key={s.minutes}
                        onClick={() => {
                            const target = snoozeFor!.reminder;
                            setSnoozeFor(null);
                            act(() => remindersApi.snooze(target._id, s.minutes), 'Could not snooze');
                        }}
                    >
                        {s.label}
                    </MenuItem>
                ))}
            </Menu>
        </DraggableDialog>
    );
};

export default ReminderDuePopup;
